import * as React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import { LucideIcon } from 'lucide-react'

export interface MetricCardProps {
  title: string
  value: string | number
  description?: string
  icon?: LucideIcon
  iconColor?: 'gold' | 'purple' | 'blue' | 'green' | 'orange'
  badge?: {
    text: string
    variant?: 'default' | 'secondary' | 'destructive' | 'outline'
  }
  trend?: {
    value: string
    isPositive?: boolean
  }
  className?: string
}

const iconColorMap = {
  gold: 'text-agentela-gold',
  purple: 'text-agentela-purple', 
  blue: 'text-agentela-blue',
  green: 'text-agentela-green',
  orange: 'text-agentela-orange'
}

export const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  ({ title, value, description, icon: Icon, iconColor = 'gold', badge, trend, className, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn('', className)} {...props}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <div className="flex items-center space-x-2">
            {Icon && <Icon className={cn('h-4 w-4', iconColorMap[iconColor])} />}
            {badge && (
              <Badge variant={badge.variant || 'secondary'} className="text-xs">
                {badge.text}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gradient-gold-purple">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {(description || trend) && (
            <div className="flex items-center justify-between">
              {description && (
                <p className="text-xs text-muted-foreground">{description}</p>
              )}
              {trend && (
                <p className={cn(
                  'text-xs font-medium',
                  trend.isPositive ? 'text-agentela-green' : 'text-agentela-orange'
                )}>
                  {trend.value}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    )
  }
)

MetricCard.displayName = 'MetricCard'
